import * as React from 'react';
import styled from 'styled-components';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import LooksTileUiKit from '@westwing/ui-kit/LooksTile';
import { useGaTracking } from './useTracking';
import * as Styled from 'Client/components/ProductDetailPage/LooksInfusionSlider/LooksInfusionSlider.styles';
import { useGaTracking as useGaTrackingPdp } from 'Client/components/ProductDetailPage/LooksInfusionSlider/useTracking';
import useInView from 'Client/hooks/dom/useInView';
import { Maybe } from 'Common/types/utility';
import useBellaTranslator from 'Client/hooks/useTranslator';
import { useBellaDispatch, useBellaSelector } from 'Client/redux/hooks';
import { setLDPKnownInfoAction } from 'Client/redux/lookDetail/actions';
import SliderTile, { SliderTileItem } from 'Client/components/Generic/ContentInfusionsSlider/SliderTile';
import ContentInfusionsSlider, {
    contentfulFormatter,
    ImageHeights,
} from 'Client/components/Generic/ContentInfusionsSlider';
import LastSlideCard from 'Client/components/Generic/ContentInfusionsSlider/LastSlideCard';
import { looksSliderRatios, looksSliderLandscapeRatios } from 'Client/constants/aspectRatios';
import { LookSliderItem } from 'Client/redux/looksSlider/types';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { WishlistBadge } from 'Client/components/Looks/WishlistBadge';

const Wrapper = styled.div`
    ${forDesktop`
        margin: 0 20px;
    `}
`;

type EnhancedSliderTileItemType = SliderTileItem & { slug: string; trackingName: string };

const LooksSliderPromotion = () => {
    const dispatch = useBellaDispatch();
    const { p100NewLooksWishlist } = useFeatureToggle();

    const t = useBellaTranslator();
    const looks = useBellaSelector(state => state.looksSlider.looks) || [];
    const {
        homepageItems: { promotedLook },
    } = useBellaSelector(state => state.homepage);
    const filteredLooks = looks.filter(look => look.urlKey !== promotedLook.slug);

    const { trackLookSliderWishlist } = useGaTrackingPdp();
    const { trackLookSliderImpression, trackLooksSliderClick, trackBannerImpression } = useGaTracking();

    const onLookClick = (look: LookSliderItem, trackingName: string) => {
        dispatch(setLDPKnownInfoAction(look));
        trackLooksSliderClick(trackingName);
    };

    const containerRef = React.useRef<Maybe<HTMLDivElement>>(null);
    useInView({
        containerRef,
        options: { threshold: 0.5 },
        onVisibleCallback: () => trackLookSliderImpression(),
    });

    const onWishlistBadgeClick = (newIsSelectedValue: boolean) => {
        if (newIsSelectedValue) {
            trackLookSliderWishlist(`Add Look to Wishlist`);
        } else {
            trackLookSliderWishlist(`Remove Look to Wishlist`);
        }
    };

    const mapToCards = (look: LookSliderItem, i: number): EnhancedSliderTileItemType => {
        const trackingName = `${look.title} ${i + 1}/${looks.length}`;

        const details = {
            targetUrl: `/looks/${look.urlKey}/`,
            fallbackSrc: look.imgUrl || '',
            desktopSrc: contentfulFormatter(look.imgUrl, ImageHeights.LOOKS_DESKTOP),
            mobileSrc: contentfulFormatter(look.imgUrl, ImageHeights.LOOKS_MOBILE),
            borderColor: look.color || '',
            title: look.title,
            ctaText: t('Read and Shop'),
            onClick: () => onLookClick(look, trackingName),
            isLandscape: false,
            slug: look.urlKey,
            trackingName: look.trackingName,
            trackImpressionFunction: () => trackBannerImpression(trackingName),
        };

        if (look.isLandscape && look.moodImgUrl) {
            details.fallbackSrc = look.moodImgUrl || '';
            details.desktopSrc = contentfulFormatter(look.moodImgUrl, ImageHeights.LOOKS_DESKTOP);
            details.mobileSrc = contentfulFormatter(look.moodImgUrl, ImageHeights.LOOKS_MOBILE);
            details.isLandscape = true;
        }
        return details;
    };
    const cards = filteredLooks.map(mapToCards);

    if (!cards.length) {
        return null;
    }

    return (
        <Wrapper ref={containerRef}>
            <Styled.LineSeparator />
            <ContentInfusionsSlider
                title={t('Homepage_Look_Slider_Headline')}
                data-testid="looks-slider"
                showBorder={false}
                noSideMarginsForWideScreen={false}
                ctaOptions={{ link: '/looks/', text: t('See all') }}
            >
                {cards.map((look: EnhancedSliderTileItemType) => (
                    <SliderTile
                        ratio={look.isLandscape ? looksSliderLandscapeRatios : looksSliderRatios}
                        card={look}
                        key={look.title}
                        showBorder={false}
                    >
                        {p100NewLooksWishlist && (
                            <LooksTileUiKit.TopRightBadge>
                                <WishlistBadge
                                    lookSlug={look.slug}
                                    testId="look-slider-wishlist-badge"
                                    onClick={onWishlistBadgeClick}
                                />
                            </LooksTileUiKit.TopRightBadge>
                        )}
                    </SliderTile>
                ))}
                <LastSlideCard
                    title={t('Shop the Look')}
                    ctaText={t('See all')}
                    link="/looks/"
                    ratio={looksSliderRatios}
                />
            </ContentInfusionsSlider>
        </Wrapper>
    );
};

export default LooksSliderPromotion;
